import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import {timeOut} from '@polymer/polymer/lib/utils/async.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import '@polymer/iron-localstorage/iron-localstorage.js';
import {ScopeBinding} from './behaviors/keli-binding-behavior.js';
import {CanvasDraggableBehavior} from './behaviors/canvas-draggable-behavior.js';
import {CanvasSelectionBehavior} from './behaviors/canvas-selection-behavior.js';
import {HotkeyBehavior} from './behaviors/hotkey-behavior.js';
import {loadDynamicDependency} from '../javascripts/modules/loadDynamicDependency.js';
import {ResizeObserverBehavior} from './behaviors/resize-observer-behavior.js';
import {ContextMenuBehavior} from './behaviors/context-menu.js';
import {parseInputColor} from '../javascripts/modules/colors.js';

class KeliImageViewer extends ScopeBinding(CanvasSelectionBehavior(CanvasDraggableBehavior(HotkeyBehavior(ResizeObserverBehavior(ContextMenuBehavior(PolymerElement)))))) {
  static get template() {
    return html`
      <style>
        :host {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          outline: none;
        }

        #viewportContainer {
          flex: 1;
          position: relative;
          background-color: var(--keli-theme-grey-700);
          overflow: auto;
          overflow-y: scroll;
        }

        .toolbar {
          position: relative;
          max-height: 80px;
          color: var(--keli-theme-white);
          background-color: var(--keli-theme-grey-800);
          padding: 0 10px;
          user-select: none;
          font-weight: 300;
          overflow-y: auto;
        }

        .toolbar sl-icon {
          padding: 5px;
          cursor: pointer;
          font-size: 24px;
        }

        .toolbar sl-icon:hover {
          background-color: rgba(0, 0, 0, .1);
        }

        #toolbar {
          display: grid;
          grid-gap: 10px;
          grid-template-areas:
      "manipulate transfer"
      "navigate navigate";
        }

        @media (min-width: 510px) {
          #toolbar {
            max-height: auto;
            grid-template-areas: "manipulate navigate transfer";
          }
        }

        @media (min-width: 600px) {
          #toolbar {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        #customToolbar {
          border-top: 1px solid var(--keli-theme-grey-900);
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }

        #customToolbar {
          --keli-button-text-color: var(--keli-theme-white);
          --keli-button-font-size: 1.1em;
          --keli-button-padding: 5px;
        }

        #toolbarManipulate {
          grid-area: manipulate;
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }

        #toolbarNavigation {
          grid-area: navigate;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        #toolbarTransfer {
          grid-area: transfer;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        #currentPage {
          background: transparent;
          border: none;
          padding: 0;
          color: inherit;
          font-size: inherit;
          font-weight: inherit;
          width: var(--image-viewer-filter-width, 2em);
          text-align: right;
        }

        #currentPage:focus {
          background: var(--keli-theme-grey-900);
          outline: none;
        }

        #imageViewport {
          width: 100%;
          height: 100%;
          position: absolute;
          display: block;
        }

        #imageViewport.loading {
          opacity: .4;
        }

        #pdfTextLayer {
          position: absolute;
          line-height: 1;
        }

        #pdfTextLayer > span {
          color: transparent;
          position: absolute;
          white-space: pre;
          cursor: text;
          transform-origin: 0 0;
        }
      </style>

      <iron-localstorage name="keli-image-viewer-settings" value="{{settings}}"
                         on-iron-localstorage-load-empty="initializeSettings"></iron-localstorage>

      <template is="dom-if" if="[[showToolbar]]">
        <div id="toolbar" class="toolbar">
          <div id="toolbarManipulate">
            <sl-icon name="magnify-plus-outline" title="Zoom In (+)" on-click="handleZoomIn"></sl-icon>
            <sl-icon name="magnify-minus-outline" title="Zoom Out (-)" on-click="handleZoomOut"></sl-icon>
            <sl-icon name="aspect-ratio" title="Toggle Page Zoom (W or P)" on-click="handleToggleZoomFit"></sl-icon>
            <sl-icon name="rotate-left" title="Rotate Left (L)" on-click="handleRotateLeft"></sl-icon>
            <sl-icon name="rotate-right" title="Rotate Right (R)" on-click="handleRotateRight"></sl-icon>
          </div>
          <div id="toolbarNavigation">
            <sl-icon name="first-page" title="Jump to First Page (Home)" on-click="goToFirstPage"></sl-icon>
            <sl-icon name="chevron-left" title="Previous Page (Page Up)" on-click="goToPreviousPage"></sl-icon>
            <input type="number" id="currentPage" value="{{currentPage::input}}" on-focus="handleCurrentPageFocus"
                   min="1" max$="[[totalPages]]" step="1">
            <span id="totalPages">/&nbsp;&nbsp;[[totalPages]]</span>
            <sl-icon name="chevron-right" title="Next Page (Page Down)" on-click="goToNextPage"></sl-icon>
            <sl-icon name="last-page" title="Jump to Last Page (End)" on-click="goToLastPage"></sl-icon>
          </div>
          <div id="toolbarTransfer">
            <template is="dom-if" if="[[downloadAction]]">
              <sl-icon name="file-download" title="Download Document (Alt+D)" on-click="handleDownload"></sl-icon>
            </template>
            <template is="dom-if" if="[[emailAction]]">
              <sl-icon name="email" title="Email Document (Alt+E)" on-click="handleEmail"></sl-icon>
            </template>
            <template is="dom-if" if="[[printAction]]">
              <sl-icon name="print" title="Print (Alt+P)" on-click="handlePrint"></sl-icon>
            </template>
          </div>
        </div>
      </template>

      <div id="customToolbar" class="toolbar">
        <slot name="toolbar"></slot>
      </div>

      <div id="viewportContainer" on-scroll="handleScrollOrZoom" on-click="handleClick">
        <canvas id="imageViewport"></canvas>
        <div id="pdfTextLayer"></div>
      </div>`;
  }

  static get is() {
    return 'keli-image-viewer'
  }

  static get properties() {
    return {
      contextMenuEnabled: {type: Boolean, value: true, scopevar: 'contextMenu'},
      contextMenuItems: {type: Array, computed: 'computeContextMenuItems(printAction, downloadAction, emailAction)'},
      currentPage: {type: Number, scopevar: 'page', notify: true, observer: 'currentPageChanged'},
      downloadAction: {type: Boolean, value: true, scopevar: true},
      dynamicDependenciesLoaded: {type: Boolean},
      emailAction: {type: Boolean, value: true, scopevar: true},
      fitTo: {type: String, scopevar: true, observer: 'fitToChanged'},
      images: {type: Array, scopevar: true},
      keliControlTypes: {type: Array, value: ['imageviewer']},
      maximumLoadedDocuments: {type: Number, value: 20},
      needsDataLoaded: {type: Boolean, value: true},
      printAction: {type: Boolean, value: true, scopevar: true},
      removeChildrenFromPanelTabOrder: {type: Boolean, value: true},
      selectionMode: {type: Boolean, scopevar: 'selection', observer: 'selectionModeChanged'},
      showToolbar: {type: Boolean, value: true, computed: 'computeShowToolbar(toolbar)'},
      toolbar: {type: Array, scopevar: true},
      totalPages: {type: Number, observer: 'totalPagesChanged'},
      _virtualPages: {
        type: Array,
        observer: 'virtualPagesChanged',
        computed: 'computeVirtualPages(images, dynamicDependenciesLoaded)'
      },
      _PDFWorker: {type: Object},
      _loadDocumentPromises: {type: Object},
      settings: {
        type: Object, notify: true, value() {
          return {fitTo: 'page'};
        }
      },
      viewport: {type: Object, scopevar: true, observer: 'viewportChanged'}
    }
  }

  initializeSettings() {
    this.settings = {fitTo: 'page'};
  }

  constructor() {
    super();
    this.addEmitToKeli([
      'imageViewerClick',
      'imageViewerDownload',
      'imageViewerEmail',
      'imageViewerPageChanged',
      'imageViewerPrint',
      'imageViewerRotate',
      'imageViewerSelectionShape',
      'imageViewerShapeChanged',
      'imageViewerZoom',
      'keliBlur',
      'keliFocus'
    ]);
  }

  async connectedCallback() {
    super.connectedCallback();
    this._ensureAttribute('tabindex', '0');
    this.classList.add('focusable-control');
    this._loadDocumentPromises = {};

    // Draggable listeners must be set up first so we can prevent selection handlers.
    this.canvasDraggableContainer = this.$.viewportContainer;
    this.canvasDraggableElement = this.$.imageViewport;

    this.canvasSelectionContainer = this.$.viewportContainer;
    this.canvasSelectionElement = this.$.imageViewport;
    this.canvasSelectionEnabled = true;

    this.contextMenuSelector = '#imageViewport';

    this.addEventListener('blur', this.handleBlur);
    this.addEventListener('focus', this.handleFocus);
    this.addEventListener('focusElement', this.handleFocusElement);
    this.addEventListener('contextmenu', this.handleContextMenu);
    this.addEventListener('imageViewerZoom', this.handleScrollOrZoom);
    this.addEventListener('registerPanelHotkey', this.handleRegisterPanelHotkey);

    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.previous, {
      down: () => this.goToPreviousPage()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.next, {
      down: () => this.goToNextPage()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.first, {
      down: () => this.goToFirstPage()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.last, {
      down: () => this.goToLastPage()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.focusPageNumber, {
      down: () => {
        const currentPageElement = this.shadowRoot.querySelector('#currentPage');
        currentPageElement?.focus();
        currentPageElement?.select();
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.download, {
      down: () => this.handleDownload()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.email, {
      down: () => this.handleEmail()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.print, {
      down: () => this.handlePrint()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.zoomOut, {
      down: async () => await this.handleZoomOut()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.zoomIn, {
      down: async () => await this.handleZoomIn()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.fitToWidth, {
      down: async () => {
        const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
        if (virtualPage?.reader) {
          if (this.fitTo !== virtualPage.reader.FIT_WIDTH) {
            this.fitTo = virtualPage.reader.FIT_WIDTH;
          } else {
            await virtualPage.reader.zoomToFit();
          }
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.fitToPage, {
      down: async () => {
        const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
        if (virtualPage?.reader) {
          if (this.fitTo !== virtualPage.reader.FIT_PAGE) {
            this.fitTo = virtualPage.reader.FIT_PAGE;
          } else {
            await virtualPage.reader.zoomToFit();
          }
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.rotateLeft, {
      down: async () => await this.handleRotateLeft()
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.rotateRight, {
      down: async () => await this.handleRotateRight()
    });

    // Manually control the expected browser scrolling behavior
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.scrollUp, {
      down: () => this.moveScrollPosition({up: true})
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.scrollRight, {
      down: () => this.moveScrollPosition({right: true})
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.scrollDown, {
      down: () => this.moveScrollPosition({down: true})
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.scrollLeft, {
      down: () => this.moveScrollPosition({left: true})
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.scrollUpBig, {
      down: () => this.moveScrollPosition({up: true, page: true})
    });
    this.registerHotkey(KeliInterface.Hotkeys.imageViewer.scrollDownBig, {
      down: () => this.moveScrollPosition({down: true, page: true})
    });

    await loadDynamicDependency([
      {global: window.pdfjsLib, path: '/javascripts/pdfjs/pdf.min.mjs', isModule: true}
    ]);
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = `/javascripts/pdfjs/pdf.worker.min.mjs?bust=${globalThis._loadingDynamicDependenciesBust}`;
    this.dynamicDependenciesLoaded = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('blur', this.handleBlur);
    this.removeEventListener('focus', this.handleFocus);
    this.removeEventListener('focusElement', this.handleFocusElement);
    this.removeEventListener('contextmenu', this.handleContextMenu);
    this.removeEventListener('imageViewerZoom', this.handleScrollOrZoom);
    this.removeEventListener('registerPanelHotkey', this.handleRegisterPanelHotkey);

    this.images = null;
    this._PDFWorker?.destroy();
    this._PDFWorker = null;
    this._loadDocumentPromises = null;
    KeliInterface.TIFFWorkerPool?.destroyOwner(this);
  }

  fireDataLoaded() {
    if (!this.dataLoaded) {
      this.dataLoaded = true;
      this.dispatchEvent(new CustomEvent('dataLoaded', {
        bubbles: true, composed: true,
        detail: {element: this}
      }));
    }
  }

  handleCurrentPageFocus() {
    const currentPageElement = this.shadowRoot.querySelector('#currentPage');
    if (currentPageElement) {
      afterNextRender(currentPageElement, () => {
        currentPageElement.select();
      });
    }
  }

  handleElementResized() {
    this._handleElementResizedJob = Debouncer.debounce(this._handleElementResizedJob, timeOut.after(10), () => {
      afterNextRender(this, async () => {
        if (this.currentPage) {
          await this.renderPageNumber(this.currentPage, this.currentPage);
        }
      });
    });
  }

  getPointAfterRotation(x, y, degrees, areaWidth, areaHeight) {
    const radians = degrees * (Math.PI / 180);
    if (!radians) {
      return {x, y};
    }
    let newX = Math.round((x * Math.cos(radians)) + (y * Math.sin(radians)));
    let newY = Math.round((-x * Math.sin(radians)) + (y * Math.cos(radians)));
    newX = newX < 0 ? areaWidth + newX : newX;
    newY = newY < 0 ? areaHeight + newY : newY;
    return {x: newX, y: newY};
  }

  getRectangleAfterRotation(srcX, srcY, srcWidth, srcHeight, degrees, areaWidth, areaHeight) {
    let {x: newAreaWidth, y: newAreaHeight} = this.getPointAfterRotation(areaWidth, areaHeight, degrees, 0, 0);
    newAreaWidth = Math.abs(newAreaWidth);
    newAreaHeight = Math.abs(newAreaHeight);
    const {x, y} = this.getPointAfterRotation(srcX, srcY, degrees, newAreaWidth, newAreaHeight);
    const {
      x: width,
      y: height
    } = this.getPointAfterRotation(srcX + srcWidth, srcY + srcHeight, degrees, newAreaWidth, newAreaHeight);
    return {x, y, width: width - x, height: height - y};
  }

  async handleCanvasDraggableMove(event, {shape, ...dragged}) {
    const {reader} = this.getVirtualPageFromNumber(this.currentPage);
    if (!reader) {
      return;
    }

    this.preventNextClickEvent = true;

    const scaled = this.getScaledRectangleInfo(dragged);
    shape.x = scaled.x;
    shape.y = scaled.y;

    await reader.redraw();
  }

  async handleCanvasDraggableFinished(event, overlay) {
    await this.handleCanvasDraggableMove(event, overlay);

    const final = {...overlay.shape};
    delete final._loadedImage;

    this.dispatchEvent(new CustomEvent('imageViewerShapeChanged', {detail: final}));
  }

  async handleCanvasSelectionFinished(event, shape) {
    shape = this.normalizeShapeInfo(shape);

    const info = this.getScaledRectangleInfo(shape);
    if (!info) {
      return;
    }

    // Assign the modifiers to the detail event.
    info.leftMouse = (event.button === 0);
    info.rightMouse = (event.button === 2);
    info.ctrlKey = event.ctrlKey;
    info.shiftKey = event.shiftKey;
    info.metaKey = event.metaKey;
    info.altKey = event.altKey;

    // Handle zooming the viewer based on the drag-selected region.
    let modifiersPressed = (event.ctrlKey || event.shiftKey || event.metaKey || event.altKey);
    if (!modifiersPressed && info.leftMouse && (shape.width > 50 || shape.height > 50)) {
      await this.zoomToSelection(this.getScaledRectangleInfo(shape, true));
    }

    this.dispatchEvent(new CustomEvent('imageViewerSelectionShape', {detail: info}));
  }

  goToFirstPage() {
    this.currentPage = 1;
  }

  goToPreviousPage() {
    if (this.currentPage && this.currentPage > 1) {
      --this.currentPage;
    }
  }

  goToNextPage() {
    if (this.currentPage && this.currentPage < this.totalPages) {
      ++this.currentPage;
    }
  }

  goToLastPage() {
    this.currentPage = this.totalPages;
  }

  handleBlur(event) {
    this.dispatchEvent(new CustomEvent('keliBlur', {bubbles: true, composed: true, detail: {element: this, event}}));
  }

  handleFocus(event) {
    this.resetHotkeyState();
    this.dispatchEvent(new CustomEvent('keliFocus', {bubbles: true, composed: true, detail: {element: this, event}}));
  }

  handleFocusElement() {
    this.focus();
  }

  handleContextMenu(event) {
    if (this.contextMenuEnabled === false) {
      this.handleClick(event);
      event.preventDefault();
      return;
    }
  }

  handleScrollOrZoom() {
    this._handleScrollOrZoomJob = Debouncer.debounce(this._handleScrollOrZoomJob, timeOut.after(100), () => {
      this.setCurrentViewportInfo();
    });
  }

  handleRegisterPanelHotkey(event) {
    event.stopPropagation();
    this.registerHotkey({
      'keys': event.detail.keys,
      'this': event.detail.scope || undefined,
      'is_unordered': true,
      'prevent_repeat': true
    }, {
      down: () => {
        event.detail.scope?.activate?.(event);
      }
    });
  }

  handleClick(event) {
    event.stopPropagation();

    this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));
    
    if (this.preventNextClickEvent) {
      this.preventNextClickEvent = false;
      return;
    }

    const bounds = this.$.imageViewport.getBoundingClientRect();
    const info = this.getScaledRectangleInfo({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
      width: 0,
      height: 0
    });
    if (!info) {
      return;
    }

    // Assign the modifiers to the detail event.
    info.leftMouse = (event.button === 0);
    info.rightMouse = (event.button === 2);
    info.ctrlKey = event.ctrlKey;
    info.shiftKey = event.shiftKey;
    info.metaKey = event.metaKey;
    info.altKey = event.altKey;

    this.dispatchEvent(new CustomEvent('imageViewerClick', {detail: info}));
  }

  handleDownload(event) {
    if (event instanceof MouseEvent) {
      event.stopPropagation();
    }
    if (this.images && this.downloadAction) {
      this.dispatchEvent(new CustomEvent('imageViewerDownload', {detail: {viewport: this.getScaledViewportInfo()}}));
    }
  }

  handleEmail(event) {
    if (event instanceof MouseEvent) {
      event.stopPropagation();
    }
    if (this.images && this.emailAction) {
      this.dispatchEvent(new CustomEvent('imageViewerEmail', {detail: {viewport: this.getScaledViewportInfo()}}));
    }
  }

  handlePrint(event) {
    if (event instanceof MouseEvent) {
      event.stopPropagation();
    }
    if (this.images && this.printAction) {
      this.dispatchEvent(new CustomEvent('imageViewerPrint', {detail: {viewport: this.getScaledViewportInfo()}}));
    }
  }

  async handleZoomIn(event) {
    if (event instanceof MouseEvent) {
      event.stopPropagation();
    }
    const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
    if (virtualPage.reader) {
      await virtualPage.reader.zoomIn();
      this.dispatchEvent(new CustomEvent('imageViewerZoom', {detail: {scale: virtualPage.reader.currentZoomScale}}));
    }
  }

  async handleZoomOut(event) {
    if (event instanceof MouseEvent) {
      event.stopPropagation();
    }
    const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
    if (virtualPage.reader) {
      await virtualPage.reader.zoomOut();
      this.dispatchEvent(new CustomEvent('imageViewerZoom', {detail: {scale: virtualPage.reader.currentZoomScale}}));
    }
  }

  async zoomToSelection(shape) {
    const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
    if (virtualPage?.reader) {
      const newScale = Math.min(virtualPage.reader.WIDTH / shape.width, virtualPage.reader.HEIGHT / shape.height);
      await virtualPage.reader.zoomToScale(newScale, shape.x, shape.y);
      this.dispatchEvent(new CustomEvent('imageViewerZoom', {detail: {scale: virtualPage.reader.currentZoomScale}}));
    }
  }

  handleToggleZoomFit() {
    const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
    if (virtualPage?.reader) {
      let fitToValue = virtualPage.reader.FIT_PAGE;
      if (this.settings.fitTo === fitToValue) {
        fitToValue = virtualPage.reader.FIT_WIDTH;
      }
      this.fitTo = fitToValue;
    }
  }

  async handleRotateLeft(event) {
    if (event instanceof MouseEvent) {
      event.stopPropagation();
    }
    const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
    if (virtualPage?.reader) {
      await virtualPage.reader.rotate(-90);
      this.dispatchEvent(new CustomEvent('imageViewerRotate', {detail: {rotation: virtualPage.reader.currentRotation}}));
    }
  }

  async handleRotateRight(event) {
    if (event instanceof MouseEvent) {
      event.stopPropagation();
    }
    const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
    if (virtualPage?.reader) {
      await virtualPage.reader.rotate(90);
      this.dispatchEvent(new CustomEvent('imageViewerRotate', {detail: {rotation: virtualPage.reader.currentRotation}}));
    }
  }

  moveScrollPosition({up, right, down, left, page} = {}) {
    const size = 100;
    const element = this.$.viewportContainer;
    let topPosition = element.scrollTop;
    let leftPosition = element.scrollLeft;

    if (up) {
      if (page) {
        topPosition -= element.clientHeight - size;
      } else {
        topPosition -= size;
      }
    }
    if (right) {
      leftPosition += size;
    }
    if (down) {
      if (page) {
        topPosition += element.clientHeight - size;
      } else {
        topPosition += size;
      }
    }
    if (left) {
      leftPosition -= size;
    }

    topPosition = Math.max(Math.min(topPosition, element.scrollHeight - element.clientHeight), 0);
    leftPosition = Math.max(Math.min(leftPosition, element.scrollWidth - element.clientWidth), 0);

    element.scrollTo({
      top: topPosition,
      left: leftPosition,
      behavior: 'instant'
    });
  }

  computeContextMenuItems(printAction, downloadAction, emailAction) {
    const items = [];
    if (printAction) {
      items.push({caption: 'Print', action: this.handlePrint});
    }
    if (downloadAction) {
      items.push({caption: 'Download', action: this.handleDownload});
    }
    if (emailAction) {
      items.push({caption: 'Email', action: this.handleEmail});
    }
    return items;
  }

  computeIconActive(active) {
    return (active ? 'active' : '');
  }

  computeShowToolbar(toolbar) {
    return toolbar !== false;
  }

  computeVirtualPages(images, dynamicDependenciesLoaded) {
    if (!images || !dynamicDependenciesLoaded) {
      return;
    }

    const pages = [];
    for (const image of images) {
      if (!image.clientpath) {
        continue;
      }

      const loaded = this._virtualPages?.find(page => page.image.clientpath === image.clientpath);
      if (loaded?.image) {
        if (loaded.reader) {
          loaded.reader.overlayShapes = image.shapes;
        }
        Object.assign(loaded.image, image);
        pages.push(loaded);
      } else {
        pages.push({
          extension: image.clientpath.split('.').pop().toLowerCase(),
          reader: null,
          image
        });
      }
    }
    return pages;
  }

  computeTotalPages() {
    this.totalPages = this._virtualPages?.reduce((a, b) => a + ((b.reader ? 1 : b?.image?.pages || 1)), 0) || 0;
  }

  async loadImage(path) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', function (event) {
        resolve(getEventTarget(event));
      });

      image.addEventListener('error', function () {
        reject(`Failed to load "${path}"`);
      });

      image.src = path;
    });
  }

  setupTiffWorkerPool(count = 0) {
    if (!KeliInterface.TIFFWorkerPool) {
      KeliInterface.TIFFWorkerPool = new TIFFWorkerPool();
    }
    KeliInterface.TIFFWorkerPool.startWorkers(this, count);
  }

  async loadTiff(path, virtualPage) {
    this.setupTiffWorkerPool(1);

    const {messageId, promise} = KeliInterface.TIFFWorkerPool.load(path);
    promise.finally(() => {
      if (this._loadDocumentPromises) {
        delete this._loadDocumentPromises[messageId];
      }
    });

    virtualPage.readerLoading = messageId;
    this._loadDocumentPromises[messageId] = promise;
    return promise;
  }

  async fitToChanged(newValue) {
    this.set('settings.fitTo', newValue);
    if (newValue && this.currentPage) {
      const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
      if (virtualPage?.reader) {
        await virtualPage.reader.zoomToFit();
        this.dispatchEvent(new CustomEvent('imageViewerZoom', {detail: {scale: virtualPage.reader.currentZoomScale}}));
      }
    }
  }

  async virtualPagesChanged(newValue, oldValue) {
    this.computeTotalPages();

    if (!newValue || !newValue.length) {
      this.currentPage = 0;
      const context = this.$.imageViewport.getContext('2d');
      context.clearRect(0, 0, context.canvas.width, context.canvas.height);
      this.setCurrentViewportInfo();
      this.fireDataLoaded();
      return;
    }

    let page = this.currentPage || 1;
    if (page > this.totalPages) {
      page = this.totalPages;
    }

    if (this.currentPage === page) {
      await this.renderPageNumber(this.currentPage, this.currentPage);
    } else {
      this.currentPage = page;
    }

    if (oldValue && Array.isArray(oldValue)) {
      for (const page of oldValue) {
        if (page?.reader?.destroy) {
          await page.reader.destroy.destroy();
          page.reader = null;
        }
      }
    }
  }

  async currentPageChanged(newValue, oldValue) {
    if (newValue) {
      await this.renderPageNumber(newValue, oldValue);
    }

    const virtualPage = this.getVirtualPageFromNumber(newValue);
    this.dispatchEvent(new CustomEvent('imageViewerPageChanged', {
      detail: {
        image: virtualPage?.image
      }
    }));

    if (this._virtualPages) {
      this.cleanDocumentsSurroundingPageNumber(newValue);
      await this.loadDocumentsSurroundingPageNumber(newValue);
    }
  }

  selectionModeChanged(newValue) {
    let enabled = true;
    const buttons = [];

    switch (newValue) {
      case 'none':
        enabled = false;
        break;
      case 'both':
        buttons.push(0, 2);
        break;
      case 'right':
        buttons.push(2);
        break;
      case 'left':
      default:
        buttons.push(0);
    }
    this.canvasSelectionEnabled = enabled;
    this.canvasSelectionButtons = buttons;
  }

  totalPagesChanged(newValue) {
    if (newValue) {
      const length = newValue.toString().length;
      this.updateStyles({'--image-viewer-filter-width': `${length < 2 ? 2 : length}em`});
    }
  }

  async viewportChanged(newValue) {
    if (!newValue) {
      return;
    }

    const current = this.getCurrentViewportInfo();
    if (current.scale !== newValue.scale || current.rotation !== newValue.rotation) {
      const reader = this.getVirtualPageFromNumber(this.currentPage)?.reader;
      reader.manualZoomScale = newValue.scale;
      reader.currentRotation = newValue.rotation;
      return await reader?.renderPage(reader.currentPage, newValue.x, newValue.y);
    }

    if (current.x !== newValue.x) {
      this.$.viewportContainer.scrollLeft = newValue.x;
    }
    if (current.y !== newValue.y) {
      this.$.viewportContainer.scrollTop = newValue.y;
    }
  }

  getVirtualPageFromNumber(pageNumber) {
    if (!this._virtualPages || !pageNumber) {
      return;
    }
    return this._virtualPages[pageNumber - 1];
  }

  setCurrentViewportInfo() {
    const current = this.getCurrentViewportInfo();

    if (current) {
      const virtualPage = this.getVirtualPageFromNumber(this.currentPage);
      current.image = virtualPage.image;
      current.page = virtualPage.page;
    }

    this.viewport = current;
  }

  getCurrentViewportInfo() {
    const reader = this.getVirtualPageFromNumber(this.currentPage)?.reader;
    if (!reader) {
      return;
    }
    return {
      type: 'rectangle',
      x: this.$.viewportContainer.scrollLeft,
      y: this.$.viewportContainer.scrollTop,
      width: this.$.viewportContainer.clientWidth,
      height: this.$.viewportContainer.clientHeight,
      scale: reader?.currentZoomScale,
      rotation: reader?.currentRotation
    };
  }

  getScaledViewportInfo() {
    return this.getScaledRectangleInfo(this.getCurrentViewportInfo());
  }

  getScaledRectangleInfo(rectangle, ignoreRotation = false) {
    const {image, reader} = this.getVirtualPageFromNumber(this.currentPage) || {};
    if (!image || !reader) {
      return;
    }

    // Copy the rectangle so we aren't modifying the original object.
    rectangle = {...rectangle};

    rectangle.image = image;
    rectangle.page = reader.currentPage;
    rectangle.scale = 1;

    if (!ignoreRotation && reader.currentRotation) {
      const newShape = this.getRectangleAfterRotation(rectangle.x, rectangle.y, rectangle.width, rectangle.height, reader.currentRotation, this.$.imageViewport.width, this.$.imageViewport.height);
      Object.assign(rectangle, newShape);
    }

    const scale = 1 / (reader.currentZoomScale || 1);
    rectangle.x = rectangle.x * scale;
    rectangle.y = rectangle.y * scale;
    rectangle.width = rectangle.width * scale;
    rectangle.height = rectangle.height * scale;

    return this.normalizeShapeInfo(rectangle);
  }

  normalizeShapeInfo(rectangle) {
    rectangle = {...rectangle};

    if (rectangle.width < 0) {
      rectangle.width = Math.abs(rectangle.width);
      rectangle.x = rectangle.x - rectangle.width;
    }
    if (rectangle.height < 0) {
      rectangle.height = Math.abs(rectangle.height);
      rectangle.y = rectangle.y - rectangle.height;
    }
    return rectangle;
  }

  async renderPageNumber(pageNumber, oldPageNumber) {
    const oldPage = oldPageNumber && this.getVirtualPageFromNumber(oldPageNumber) || null;
    if (oldPage?.reader?.pageRendering) {
      oldPage.reader.pageRendering.cancel();
      oldPage.reader.pageRendering = null;
    }

    // Set up some defaults from this.viewport if this is the first render.
    const defaultViewport = {};
    if (!oldPage && this.viewport) {
      defaultViewport.scale = this.viewport.scale;
      defaultViewport.x = this.viewport.x;
      defaultViewport.y = this.viewport.y;
    }

    const virtualPage = this.getVirtualPageFromNumber(pageNumber);
    if (virtualPage) {
      if (!virtualPage?.reader?.hasImage()) {
        this.$.imageViewport.classList.add('loading');
        await this.loadDocument(virtualPage);
        if (!this.isConnected || this.currentPage !== pageNumber) {
          return;
        }
      }
      if (!virtualPage.reader) {
        return;
      }

      virtualPage.reader.manualZoomScale = defaultViewport.scale || oldPage?.reader?.manualZoomScale || virtualPage.reader.manualZoomScale;
      virtualPage.reader.currentRotation = oldPage?.reader?.currentRotation || virtualPage.reader.currentRotation;

      virtualPage.reader.currentPage = virtualPage.page;
      virtualPage.reader.setSize(this.$.viewportContainer.clientWidth, this.$.viewportContainer.clientHeight);
      await virtualPage.reader.renderPage(virtualPage.page, defaultViewport.x, defaultViewport.y);
      this.$.imageViewport.classList.remove('loading');
    } else {
      this.setCurrentViewportInfo();
    }
  }

  async loadDocument(virtualPage) {
    if (virtualPage.reader?.hasImage()) {
      return;
    }
    if (virtualPage.readerLoading) {
      return this._loadDocumentPromises[virtualPage.readerLoading];
    }

    const pages = [];
    try {
      virtualPage.readerLoading = true;

      if (virtualPage.extension === 'pdf') {
        await this.loadDocumentPDF(virtualPage, pages);
      } else if (['tif', 'tiff'].includes(virtualPage.extension)) {
        await this.loadDocumentTIFF(virtualPage, pages);
      } else {
        await this.loadDocumentImage(virtualPage, pages);
      }
    } catch (error) {
      const errorCanvas = document.createElement('canvas');
      errorCanvas.width = 1000;
      errorCanvas.height = 1000;
      const errorContext = errorCanvas.getContext('2d');
      errorContext.fillStyle = '#F5F5F5';
      errorContext.fillRect(0, 0, errorCanvas.width, errorCanvas.height);
      errorContext.fillStyle = '#D32F2F';
      errorContext.font = '32px sans-serif';
      errorContext.textAlign = 'center';
      errorContext.fillText('Loading Error', 500, 460);
      errorContext.fillStyle = '#616161';
      errorContext.font = '20px sans-serif';
      errorContext.textAlign = 'center';
      errorContext.fillText(error.message, 500, 500);
      pages.push({
        reader: new ImageReader({
          viewer: this,
          viewportContainer: this.$.viewportContainer,
          canvas: this.$.imageViewport,
          image: errorCanvas
        }), page: 1, image: virtualPage.image
      });
    } finally {
      virtualPage.readerLoading = false;
    }

    if (pages.length) {
      const firstPage = pages.shift();
      Object.assign(virtualPage, firstPage);
    }
    if (pages.length) {
      const existingIndex = this._virtualPages.findIndex(page => page === virtualPage);
      this._virtualPages.splice(existingIndex + 1, 0, ...pages);
    }
    this.computeTotalPages();
  }

  async loadDocumentPDF(virtualPage, pages) {
    if (!this._PDFWorker) {
      this._PDFWorker = new window.pdfjsLib.PDFWorker();
    }

    const pdf = await window.pdfjsLib.getDocument({
      url: virtualPage.image.clientpath,
      worker: this._PDFWorker
    }).promise;

    if (pdf.numPages === 0) {
      throw new Error('The document is empty and contains no pages.');
    }

    if (virtualPage.reader) {
      virtualPage.reader.setImage(pdf);
    } else {
      virtualPage.reader = new PDFReader({
        viewer: this,
        pdf: pdf,
        viewportContainer: this.$.viewportContainer,
        canvas: this.$.imageViewport,
        textLayer: this.$.pdfTextLayer,
        enableTextSelection: false,
        overlayShapes: virtualPage.image.shapes
      });

      for (let i = 1; i <= pdf.numPages; ++i) {
        const existingPage = this._virtualPages.find(page => page.reader === virtualPage.reader && page.page === i);
        if (!existingPage) {
          pages.push(Object.assign({}, virtualPage, {page: i, readerLoading: false}));
        }
      }
    }
  }

  async loadDocumentTIFF(virtualPage, pages) {
    const imageData = await this.loadTiff(virtualPage.image.clientpath, virtualPage);
    for (const [i, imageInstance] of imageData.entries()) {
      const pageNumber = i + 1;

      const existingPage = this._virtualPages.find(page => page.reader === virtualPage.reader && page.page === pageNumber);
      if (existingPage) {
        existingPage.reader.setImage(imageInstance);
      } else {
        pages.push({
          reader: new ImageReader({
            viewer: this,
            viewportContainer: this.$.viewportContainer,
            canvas: this.$.imageViewport,
            image: imageInstance,
            overlayShapes: virtualPage.image.shapes
          }),
          page: pageNumber,
          image: virtualPage.image
        });
      }
    }
  }

  async loadDocumentImage(virtualPage, pages) {
    const imageInstance = await this.loadImage(virtualPage.image.clientpath);
    if (virtualPage.reader) {
      virtualPage.reader.setImage(imageInstance);
    } else {
      pages.push({
        reader: new ImageReader({
          viewer: this,
          viewportContainer: this.$.viewportContainer,
          canvas: this.$.imageViewport,
          image: imageInstance,
          overlayShapes: virtualPage.image.shapes
        }),
        page: 1,
        image: virtualPage.image
      });
    }
  }

  cleanDocumentsSurroundingPageNumber(pageNumber) {
    if (!this._virtualPages?.length || this.currentPage !== pageNumber) {
      return;
    }
    const virtualPage = this.getVirtualPageFromNumber(pageNumber);
    if (!virtualPage) {
      return;
    }

    keliUITimingEvents && keliUITimer(`UI_ImageViewer_cleanDocumentsSurroundingPageNumber_page.${pageNumber}`);

    const distance = this.maximumLoadedDocuments / 2;
    const documents = this._virtualPages.filter(page => page.page === 1);
    const currentDocumentIndex = documents.findIndex(doc => doc.reader === virtualPage.reader);

    for (const [i, doc] of documents.entries()) {
      if (i < (currentDocumentIndex - distance) || i > (currentDocumentIndex + distance)) {
        const documentPages = this._virtualPages.filter(page => page.reader === doc.reader);
        for (const page of documentPages) {
          page.reader.setImage(null);
        }
      }
    }

    keliUITimingEvents && keliUITimer(`UI_ImageViewer_cleanDocumentsSurroundingPageNumber_page.${pageNumber}`, true);
  }

  async loadDocumentsSurroundingPageNumber(pageNumber) {
    if (!this._virtualPages?.length || this.currentPage !== pageNumber) {
      return;
    }
    if (this._virtualPages.some(page => page.readerLoading)) {
      setTimeout(async () => await this.loadDocumentsSurroundingPageNumber(pageNumber), 100);
      return;
    }
    const virtualPage = this.getVirtualPageFromNumber(pageNumber);
    if (!virtualPage) {
      return;
    }

    const distance = this.maximumLoadedDocuments / 2;
    const documents = this._virtualPages.filter(page => !page.page || page.page === 1);
    const currentDocumentIndex = documents.findIndex(doc => doc.reader === virtualPage.reader);

    const unloaded = [];
    for (const [i, doc] of documents.entries()) {
      if (!doc.reader?.hasImage() && i >= (currentDocumentIndex - distance) && i <= (currentDocumentIndex + distance)) {
        unloaded.push(doc);
      }
    }

    if (!unloaded.length) {
      return;
    }

    keliUITimingEvents && keliUITimer(`UI_ImageViewer_loadDocumentsSurroundingPageNumber_page.${pageNumber}`);

    const promises = [];

    // Setup the TIFF worker pool if needed.
    const tiffCount = unloaded.filter(page => ['tif', 'tiff'].includes(page.extension)).length;
    if (tiffCount) {
      this.setupTiffWorkerPool(tiffCount);
      const pages = unloaded.slice(0, KeliInterface.TIFFWorkerPool.workers.length);
      for (const page of pages) {
        promises.push(this.loadDocument(page));
      }
    } else {
      promises.push(this.loadDocument(unloaded.shift()));
    }

    await Promise.allSettled(promises);

    keliUITimingEvents && keliUITimer(`UI_ImageViewer_loadDocumentsSurroundingPageNumber_page.${pageNumber}`, true);

    if (!this.isConnected) return;
    setTimeout(async () => await this.loadDocumentsSurroundingPageNumber(pageNumber), 100);
  }
}

class ReaderBase {
  constructor(options) {
    this.IMAGE = null;
    this.FIT_PAGE = 'page';
    this.FIT_WIDTH = 'width';
    this.GUTTER = 3;
    this.currentRotation = 0;
    this.currentPage = 1;
    this.manualZoomScale = undefined;
    this.viewer = options.viewer;
    this.viewportContainer = options.viewportContainer;
    this.canvas = options.canvas;
    this.canvasStyle = options.canvas.style;
    this.canvasContext = options.canvas.getContext('2d');
    this.overlayShapes = options.overlayShapes;
  }

  hasImage() {
    return !!this.IMAGE;
  }

  setImage(image) {
    this.IMAGE = image;
  }

  setSize(width, height) {
    if (width) {
      this.WIDTH = width;
    } else if (!this.WIDTH) {
      this.WIDTH = this.viewportContainer.clientWidth;
    }
    if (height) {
      this.HEIGHT = height;
    } else if (!this.HEIGHT) {
      this.HEIGHT = this.viewportContainer.clientHeight;
    }
    this.HEIGHT -= (this.GUTTER * 2);
  }

  setViewportPosition() {
    if (this.pageWidth < this.WIDTH) {
      this.canvasStyle.left = `${(this.WIDTH - this.pageWidth) / 2}px`;
    } else {
      this.canvasStyle.left = 0;
    }
    if (this.pageHeight - (this.GUTTER * 2) < this.HEIGHT) {
      this.canvasStyle.top = `${this.GUTTER}px`;
    } else {
      this.canvasStyle.top = 0;
    }

    if (this.enableTextSelection) {
      this.textLayerDivStyle.left = this.canvasStyle.left;
      this.textLayerDivStyle.top = this.canvasStyle.top;
    }
  }

  renderPage() {
    this.viewer.setCurrentViewportInfo();
    this.viewer.fireDataLoaded();
  }

  async renderOverlayShapes(scale) {
    if (!this.overlayShapes?.length) {
      this.viewer.canvasDraggableShapes = null;
      return;
    }

    this.viewer.canvasDraggableShapes = [];
    for (const overlayShape of this.overlayShapes) {
      const shape = {...overlayShape};

      const context = this.canvasContext;
      context.save();

      this.canvasContext.scale(scale, scale);

      if (this.currentRotation) {
        const radians = this.currentRotation * Math.PI / 180;
        const halfImageWidth = this.IMAGE.width / 2;
        const halfImageHeight = this.IMAGE.height / 2;
        context.translate(Math.abs(halfImageWidth * Math.cos(radians) + halfImageHeight * Math.sin(radians)), Math.abs(halfImageHeight * Math.cos(radians) + halfImageWidth * Math.sin(radians)));
        context.rotate(radians);
        context.translate(-halfImageWidth, -halfImageHeight);
      }

      if (shape.color) {
        context.beginPath();
        context.rect(shape.x, shape.y, shape.width, shape.height);
        context.strokeStyle = parseInputColor(shape.color);
        context.lineWidth = 1;
        context.fillStyle = parseInputColor(shape.color, shape.opacity || 1);
        context.stroke();
        context.fill();
      }

      if (shape.image) {
        if (shape.opacity) {
          context.globalAlpha = shape.opacity;
        }

        // Store the loaded image as a reference on the shape to prevent multiple loads.
        if (!overlayShape._loadedImage) {
          overlayShape._loadedImage = await this.viewer.loadImage(shape.image);
        }

        context.drawImage(overlayShape._loadedImage, shape.x, shape.y, shape.width, shape.height);
      }

      context.restore();

      if (shape.draggable) {
        const screenShape = this.viewer.getRectangleAfterRotation(shape.x, shape.y, shape.width, shape.height, -this.currentRotation, this.IMAGE.width, this.IMAGE.height);
        screenShape.x = screenShape.x * scale;
        screenShape.y = screenShape.y * scale;
        screenShape.width = screenShape.width * scale;
        screenShape.height = screenShape.height * scale;

        this.viewer.canvasDraggableShapes.push({
          shape: overlayShape,
          screen: this.viewer.normalizeShapeInfo(screenShape)
        });
      }
    }
  }

  async zoomIn(step = .25) {
    this.manualZoomScale = (Math.round((this.currentZoomScale * (1 + step)) * 1000) / 1000);
    await this.renderPage();
  }

  async zoomOut(step = .25) {
    const newZoomScale = (Math.round((this.currentZoomScale * (1 - step)) * 1000) / 1000);
    if (newZoomScale > 0) {
      this.manualZoomScale = newZoomScale;
      await this.renderPage();
    }
  }

  async zoomToScale(scale, x, y) {
    if (scale > 0) {
      this.manualZoomScale = scale;
      await this.renderPage(this.currentPage, x, y);
    }
  }

  async zoomToFit() {
    this.manualZoomScale = undefined;
    await this.renderPage();
  }

  async rotate(step) {
    this.currentRotation = (this.currentRotation + step + 360) % 360;
    await this.renderPage();
  }

  async redraw() {
    await this.renderPage();
  }
}

class PDFReader extends ReaderBase {
  constructor(options) {
    super(options);

    this.setImage(options.pdf);
    if (options.enableTextSelection && options.textLayer) {
      this.textLayerDiv = options.textLayer;
      this.textLayerDivStyle = options.textLayer.style;
    }
    this.enableTextSelection = options.enableTextSelection;
    this.setSize();
  }

  async renderPage(pageNumber = this.currentPage, x, y) {
    const page = await this.IMAGE.getPage(pageNumber);
    this.IMAGE.width = page.view[2];
    this.IMAGE.height = page.view[3];
    page._pageInfo.rotate = this.currentRotation;

    let scaleWidth;
    let scaleHeight;
    let viewerViewport;
    let radians = page._pageInfo.rotate * Math.PI / 180;

    this.pageWidth = Math.abs((page.view[2] * Math.cos(radians)) + (page.view[3] * Math.sin(radians)));
    this.pageHeight = Math.abs((page.view[3] * Math.cos(radians)) + (page.view[2] * Math.sin(radians)));

    scaleWidth = Math.floor((this.WIDTH / this.pageWidth) * 10000) / 10000;
    scaleHeight = Math.floor((this.HEIGHT / this.pageHeight) * 10000) / 10000;

    if (this.manualZoomScale) {
      this.currentZoomScale = this.manualZoomScale;
    } else {
      const fitTo = this.viewer.settings.fitTo || this.FIT_PAGE;
      if (fitTo === this.FIT_PAGE) {
        this.currentZoomScale = Math.min(scaleHeight, scaleWidth);
      } else if (fitTo === this.FIT_WIDTH) {
        this.currentZoomScale = this.WIDTH / this.pageWidth;
      }
    }

    viewerViewport = page.getViewport({scale: this.currentZoomScale});
    this.pageWidth = Math.round(viewerViewport.width);
    this.pageHeight = Math.round(viewerViewport.height);

    if (this.canvas.width !== this.pageWidth) {
      this.canvas.width = this.canvasContext.width = this.pageWidth;
      this.canvasStyle.width = `${this.pageWidth}px`;
    }
    if (this.canvas.height !== this.pageHeight) {
      this.canvas.height = this.canvasContext.height = this.pageHeight;
      this.canvasStyle.height = `${this.pageHeight}px`;
    }
    this.setViewportPosition();

    if (this.enableTextSelection) {
      this.textLayerDivStyle.width = `${this.pageWidth}px`;
      this.textLayerDivStyle.height = `${this.pageHeight}px`;
    }

    // Clone the canvas, render on the cloned version, and draw the clone on the original canvas to prevent flicker when rendering.
    const renderCanvas = this.canvas.cloneNode();
    const renderContext = renderCanvas.getContext('2d');

    if (this.pageRendering) {
      this.pageRendering.cancel();
    }
    this.pageRendering = page.render({
      canvasContext: renderContext,
      viewport: viewerViewport
    });
    this.pageRendering.promise.then(async () => {
      this.pageRendering = null;

      this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.canvasContext.drawImage(renderCanvas, 0, 0);
      await this.renderOverlayShapes(this.currentZoomScale);

      if (x && y) {
        this.viewportContainer.scrollTop = y * this.currentZoomScale;
        this.viewportContainer.scrollLeft = x * this.currentZoomScale;
      }
    }).catch(error => {
      if (!error.message.startsWith('Rendering cancelled')) {
        keliUILogger(error);
      }
    });

    if (this.enableTextSelection) {
      this.textLayerDiv.innerHTML = '';
      page.getTextContent().then(textContent => {
        window.pdfjsLib.renderTextLayer({
          textContent,
          container: this.textLayerDiv,
          pageIndex: pageNumber,
          viewport: viewerViewport,
          textDivs: []
        });
      });
    }
    super.renderPage();
  }
}

class ImageReader extends ReaderBase {
  constructor(options) {
    super(options);
    this.setImage(options.image);
    this.setSize();
  }

  async renderPage(pageNumber = this.currentPage, x, y) {
    const radians = this.currentRotation * Math.PI / 180;

    this.pageWidth = Math.abs((this.IMAGE.width * Math.cos(radians)) + (this.IMAGE.height * Math.sin(radians)));
    this.pageHeight = Math.abs((this.IMAGE.height * Math.cos(radians)) + (this.IMAGE.width * Math.sin(radians)));

    const scaleWidth = Math.floor((this.WIDTH / this.pageWidth) * 10000) / 10000;
    const scaleHeight = Math.floor((this.HEIGHT / this.pageHeight) * 10000) / 10000;

    if (this.manualZoomScale) {
      this.currentZoomScale = this.manualZoomScale;
    } else {
      const fitTo = this.viewer.settings.fitTo || this.FIT_PAGE;
      if (fitTo === this.FIT_PAGE) {
        this.currentZoomScale = Math.min(scaleHeight, scaleWidth);
      } else if (fitTo === this.FIT_WIDTH) {
        this.currentZoomScale = this.WIDTH / this.pageWidth;
      }
    }

    this.pageWidth = this.pageWidth * this.currentZoomScale;
    this.pageHeight = this.pageHeight * this.currentZoomScale;

    if (this.canvas.width !== this.pageWidth) {
      this.canvas.width = this.canvasContext.width = this.pageWidth;
      this.canvasStyle.width = `${this.pageWidth}px`;
    }
    if (this.canvas.height !== this.pageHeight) {
      this.canvas.height = this.canvasContext.height = this.pageHeight;
      this.canvasStyle.height = `${this.pageHeight}px`;
    }
    this.setViewportPosition();

    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.canvasContext.save();
    this.canvasContext.scale(this.currentZoomScale, this.currentZoomScale);
    if (this.currentRotation) {
      const halfImageWidth = this.IMAGE.width / 2;
      const halfImageHeight = this.IMAGE.height / 2;
      this.canvasContext.translate(Math.abs(halfImageWidth * Math.cos(radians) + halfImageHeight * Math.sin(radians)), Math.abs(halfImageHeight * Math.cos(radians) + halfImageWidth * Math.sin(radians)));
      this.canvasContext.rotate(radians);
      this.canvasContext.translate(-halfImageWidth, -halfImageHeight);
    }
    this.canvasContext.imageSmoothingEnabled = true;
    this.canvasContext.imageSmoothingQuality = 'medium';
    this.canvasContext.drawImage(this.IMAGE, 0, 0);
    this.canvasContext.restore();

    await this.renderOverlayShapes(this.currentZoomScale);

    if (x && y) {
      this.viewportContainer.scrollTop = y * this.currentZoomScale;
      this.viewportContainer.scrollLeft = x * this.currentZoomScale;
    }
    super.renderPage();
  }
}

class TIFFWorkerPool {
  constructor() {
    this.owners = new Map();
    this.workers = [];
    this.lastUsedIndex = -1;
    this.lastMessageID = 0;
    this.resolves = new Map();
    this.rejects = new Map();
  }

  async destroyOwner(owner) {
    this.owners.delete(owner);

    // Clean up any old disconnected owners
    for (const owner of this.owners.keys()) {
      if (!owner.isConnected) {
        this.owners.delete(owner);
      }
    }

    const minWorkers = 1; // Always keep at least one worker available
    const maxWorkers = (this.owners.size ? Math.max(...this.owners.values()) : minWorkers);
    while (this.workers.length > maxWorkers) {
      // Do not remove workers if there is any pending work.
      if (this.resolves.size || this.rejects.size) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      const worker = this.workers.pop();
      worker?.terminate();
    }
  }

  startWorkers(owner, count) {
    const heap = window.performance?.memory;
    const memory = window.navigator?.deviceMemory;
    const concurrency = window.navigator?.hardwareConcurrency;

    let lowResourceEnvironment = false;
    if (heap && (heap.jsHeapSizeLimit - heap.usedJSHeapSize) <= 1073741824) { // 1GB
      lowResourceEnvironment = true;
    }
    if (memory && memory <= 4) { // 4GB
      lowResourceEnvironment = true;
    }
    if (concurrency && concurrency <= 4) { // 4 logical cores
      lowResourceEnvironment = true;
    }

    // Limit memory usage by disabling preloading of images
    if (lowResourceEnvironment) {
      owner.maximumLoadedDocuments = 1;
    }

    const dynamicCount = Math.max(Math.floor(Math.min(count, concurrency - 2 || 2)), 1);
    const finalCount = (lowResourceEnvironment ? 1 : dynamicCount);

    if (this.workers.length < finalCount) {
      this.owners.set(owner, Math.max(this.owners.get(owner) || 0, count));
    }

    while (this.workers.length < finalCount) {
      const worker = new Worker('/javascripts/tiff-worker/tiff.worker.js');
      worker.onmessage = event => {
        const {messageId, error, images} = event.data;
        if (images && !error) {
          const resolve = this.resolves.get(messageId);
          if (resolve) {
            resolve(images);
          }
        } else {
          const reject = this.rejects.get(messageId);
          if (reject) {
            reject(new Error(error));
          }
        }

        this.resolves.delete(messageId);
        this.rejects.delete(messageId);
      }
      this.workers.push(worker);
    }
  }

  load(path) {
    const messageId = ++this.lastMessageID;

    const promise = new Promise((resolve, reject) => {
      this.rejects.set(messageId, reject);
      this.resolves.set(messageId, images => {
        const tiffImages = [];
        for (const image of images) {
          const rawCanvas = document.createElement('canvas');
          rawCanvas.width = image.width;
          rawCanvas.height = image.height;

          const canvasContext = rawCanvas.getContext('2d');
          if ('ImageBitmap' in globalThis && image.imageData instanceof ImageBitmap) {
            canvasContext.drawImage(image.imageData, 0, 0);
          } else {
            const rawData = new ImageData(new Uint8ClampedArray(image.imageData), image.width, image.height);
            canvasContext.putImageData(rawData, 0, 0);
          }
          tiffImages.push(rawCanvas);
        }
        resolve(tiffImages);
      });

      const index = ++this.lastUsedIndex;
      const worker = this.workers[index] || this.workers[0];
      worker.postMessage({messageId, path});
    });

    return {messageId, promise};
  }
}

customElements.define(KeliImageViewer.is, KeliImageViewer);
export {KeliImageViewer};